import Icon from '@atoms/Icon/Icon';
import { IAccountDropdown } from '@models/IAccountDropdown';
import { IMainNavigationItem } from '@models/IHeader';
import { IconName } from '@models/IIcon';
import ILanguageSelector from '@models/ILanguageSelector';
import { ILink } from '@models/ILink';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import updateDataLayer from '@utils/helpers/gtm';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import { motion } from 'framer-motion';
import { Dispatch, SetStateAction, useState } from 'react';
import AccountMenuMobile from './AccountMenuMobile';
import LanguageSelectorMobile from './LanguageSelectorMobile';
import MobileSecondLevel from './MobileSecondLevel';

interface Props {
  readonly accountsEnabled: boolean;
  readonly accountLink: ILink;
  readonly accountDropdownNavigation: IAccountDropdown;
  readonly mainNavigation: IMainNavigationItem[];
  readonly languageSelector?: ILanguageSelector;
  readonly languageActiveIndex: number;
  readonly isOpenSecondLevel: boolean;
  readonly serviceAccountLink?: ILink;
  readonly setIsOpenSecondLevel: Dispatch<SetStateAction<boolean>>;
  readonly showAccountIcon?: boolean;
}

interface LinkComponentProps {
  readonly title: string;
  readonly href: string;
  readonly target: string;
  readonly icon?: IconName;
}

const AccountLink = ({
  title,
  href,
  target = '_self',
  icon = 'account',
}: LinkComponentProps) => (
  <a
    href={href}
    className="h5 flex w-full cursor-pointer flex-row gap-x-4 pr-5 pb-8 pl-5 text-black-100"
    target={target}
  >
    <div className="flex items-center justify-center">
      <Icon name={icon} size={IconSizeEnum.lg} className="mr-2" />
      <span>{title}</span>
    </div>

    <div className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow">
      <Icon name="east" size={IconSizeEnum.sm} />
    </div>
  </a>
);

const MobileTopLevel = (props: Props) => {
  const {
    accountsEnabled,
    accountLink,
    accountDropdownNavigation,
    mainNavigation,
    isOpenSecondLevel,
    serviceAccountLink,
    setIsOpenSecondLevel,
    languageActiveIndex,
    languageSelector,
    showAccountIcon,
  } = props;
  const [activeCtaTabIndex, setActiveCtaTabIndex] = useState(-1);
  const isInIFrame = useIsInIFrame();

  const handleLevelOneClick = (item: string) => {
    updateDataLayer({
      event: 'menuUsed',
      content_type: 'header_menu_level_1',
      main_category: item,
      sub_category: undefined,
      tertiary_category: undefined,
    });
  };

  const mobileMenuVariant = {
    opened: {
      x: '-100%',
      transition: {
        delay: 0.15,
        duration: 1.1,
        ease: [0.74, 0, 0.19, 1.02],
      },
    },
    closed: {
      x: '0%',
      transition: {
        delay: 0.35,
        duration: 0.63,
        ease: [0.74, 0, 0.19, 1.02],
      },
    },
  };

  const { loggedInMenuTitle, loggedOutMenuTitle, userAuthenticated } =
    accountDropdownNavigation;

  const conditions = {
    userAuthenticated: accountsEnabled && userAuthenticated,
    userNotAuthenticated: accountsEnabled && !userAuthenticated,
    accountsDisabledCondition: !accountsEnabled,
  };

  // ONLY RELEVANT WHEN ACCOUNTS ARE DISABLED

  const {
    url: accountPageUrl,
    target: accountPageTarget = '_self',
    name: accountText,
  } = accountLink ?? {};

  const serviceAccountLinkTarget = serviceAccountLink?.target ?? '_blank';

  return (
    <motion.div
      initial="closed"
      animate={isOpenSecondLevel ? 'opened' : 'closed'}
      variants={mobileMenuVariant}
      className="flex h-full min-w-[100%] flex-row items-start"
    >
      <motion.ul className="flex h-full w-full flex-col items-start justify-between overflow-y-auto">
        <div className="w-full">
          {mainNavigation.map(({ link, subMenuItems, title = '' }, index) =>
            link?.url ? (
              <div key={`level-1-nav-link--${link?.name}`} className="w-full">
                <motion.li
                  onClick={() => {
                    setActiveCtaTabIndex(index);
                    setIsOpenSecondLevel(true);
                    handleLevelOneClick(link?.name || '');
                  }}
                  className="h5 flex w-full cursor-pointer flex-row gap-x-4 pr-5 pb-8 pl-5 text-black-100"
                  data-testid={`CT-01-Header-Mobile-Top-Level-Category-Item-${title}`}
                >
                  <a
                    href={link.url}
                    target={isInIFrame ? '_parent' : link.target}
                    data-testid={`CT-01-Header-Mobile-Top-Level-Category-Link-${title}`}
                  >
                    {link?.name}
                  </a>

                  <div className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow">
                    <Icon name="east" size={IconSizeEnum.sm} />
                  </div>
                </motion.li>
              </div>
            ) : (
              <div key={`level-1-nav-link--${link?.name}`} className="w-full">
                <motion.li
                  onClick={() => {
                    setActiveCtaTabIndex(index);
                    setIsOpenSecondLevel(true);
                    handleLevelOneClick(title);
                  }}
                  className="h5 flex w-full cursor-pointer flex-row gap-x-4 pr-5 pb-8 pl-5 text-black-100"
                  data-testid={`CT-01-Header-Mobile-Top-Level-Category-Title-${title}`}
                >
                  {title}

                  <div className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow">
                    <Icon name="east" size={IconSizeEnum.sm} />
                  </div>
                </motion.li>

                {index === activeCtaTabIndex && (
                  <MobileSecondLevel
                    activeTab={index === activeCtaTabIndex ? subMenuItems : []}
                    secondLevelTitle={title}
                  />
                )}
              </div>
            )
          )}
        </div>
        <div className="w-full items-end">
          {showAccountIcon &&
            conditions.userAuthenticated &&
            loggedInMenuTitle && (
              <>
                <motion.button
                  onClick={() => {
                    setActiveCtaTabIndex(mainNavigation.length + 1);
                    setIsOpenSecondLevel(true);
                  }}
                  className="h5 flex w-full cursor-pointer flex-row gap-x-4 pr-5 pb-8 pl-5 text-black-100"
                >
                  <div className="flex items-center justify-center">
                    <Icon
                      name="account"
                      size={IconSizeEnum.lg}
                      className="mr-2"
                    />
                    <span>{loggedInMenuTitle}</span>
                  </div>

                  <div className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow">
                    <Icon name="east" size={IconSizeEnum.sm} />
                  </div>
                </motion.button>
                {mainNavigation.length + 1 === activeCtaTabIndex && (
                  <AccountMenuMobile
                    items={accountDropdownNavigation?.navigationDropdown}
                    {...accountDropdownNavigation}
                  />
                )}
              </>
            )}
          {conditions.userNotAuthenticated &&
            loggedOutMenuTitle &&
            accountDropdownNavigation?.loginEndpoint && (
              <AccountLink
                title={loggedOutMenuTitle}
                href={accountDropdownNavigation.loginEndpoint}
                target={isInIFrame ? '_parent' : '_self'}
              />
            )}
          {conditions.accountsDisabledCondition &&
            accountText &&
            accountPageUrl && (
              <AccountLink
                title={accountText}
                href={accountPageUrl}
                target={isInIFrame ? '_parent' : accountPageTarget}
              />
            )}
          {serviceAccountLink?.url && serviceAccountLink?.name && (
            <AccountLink
              title={serviceAccountLink.name}
              href={serviceAccountLink.url}
              target={isInIFrame ? '_parent' : serviceAccountLinkTarget}
              icon={serviceAccountLink.linkIcon}
            />
          )}
          {!!languageSelector && (
            <>
              <motion.button
                onClick={() => {
                  setActiveCtaTabIndex(mainNavigation.length + 2);
                  setIsOpenSecondLevel(true);
                }}
                className="h5 flex w-full cursor-pointer flex-row gap-x-4 pr-5 pb-8 pl-5 text-black-100"
              >
                <div className="flex items-center justify-center">
                  <Icon
                    name="language"
                    size={IconSizeEnum.lg}
                    className="mr-2"
                  />
                  <span>
                    {languageSelector.links[languageActiveIndex]?.longName ||
                      ''}
                  </span>
                </div>

                <div className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow">
                  <Icon name="east" size={IconSizeEnum.sm} />
                </div>
              </motion.button>
              {mainNavigation.length + 2 === activeCtaTabIndex && (
                <LanguageSelectorMobile
                  languageActiveIndex={languageActiveIndex}
                  languageSelector={languageSelector}
                />
              )}
            </>
          )}
        </div>
      </motion.ul>
    </motion.div>
  );
};

export default MobileTopLevel;
